"use client";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { X, Users, Grid3X3, Pencil, Check } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { ProjectCard } from './ProjectCard';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay, getFirstCollision, pointerWithin, useDroppable } from '@dnd-kit/core';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

function DraggableStackProjectCard({ project }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="w-full">
      <ProjectCard 
        project={project}
        onColorChange={() => {}} // No color change in stack view
        onPin={() => {}} // No pin functionality in stack view
        isDragActive={isDragging}
        isDropTarget={false}
      />
    </div>
  );
}

export function StackViewModal({ 
  isOpen, 
  onClose, 
  stack,
  onRemoveFromStack,
  onDeleteStack,
  onUpdateStack
}) {
  const [activeId, setActiveId] = useState(null);
  const [isDraggingOutside, setIsDraggingOutside] = useState(false);
  const [dragOverlayAnimated, setDragOverlayAnimated] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isHoveringLogo, setIsHoveringLogo] = useState(false);
  const modalRef = useRef(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Create a droppable area for the modal content (inside)
  const { setNodeRef: setModalDropRef } = useDroppable({
    id: 'modal-drop-zone'
  });


  // Combined ref for both modal bounds detection and drop zone
  const combinedRef = (node) => {
    modalRef.current = node;
    setModalDropRef(node);
  };

  // Custom collision detection that excludes the active element
  const customCollisionDetection = (args) => {
    // First, let's get all potential collisions using the default algorithm
    const pointerIntersections = pointerWithin(args);
    
    // Filter out the active element from the collisions
    const filteredCollisions = pointerIntersections.filter(
      collision => collision.id !== args.active.id
    );
    
    console.log('🎯 Custom collision detection:', {
      activeId: args.active.id,
      allCollisions: pointerIntersections.map(c => c.id),
      filteredCollisions: filteredCollisions.map(c => c.id),
      hasCollisions: filteredCollisions.length > 0
    });
    
    // If no collisions after filtering, return empty array (dragging outside)
    if (filteredCollisions.length === 0) {
      return [];
    }
    
    // Return the first collision that's not the active element
    return [getFirstCollision(filteredCollisions, 'id')].filter(Boolean);
  };

  const handleDragStart = (event) => {
    console.log('🚀 Drag started:', event.active.id);
    setActiveId(event.active.id);
    setIsDraggingOutside(false);
    setDragOverlayAnimated(false); // Reset animation state
  };

  // Animate drag overlay entry
  useEffect(() => {
    if (activeId) {
      // Small delay to allow DOM to update, then trigger animation
      const timer = setTimeout(() => {
        setDragOverlayAnimated(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setDragOverlayAnimated(false);
    }
  }, [activeId]);

  const handleDragMove = (event) => {
    const { active, over, delta } = event;
    
    // Check if mouse is inside the modal dialog bounds
    if (modalRef.current) {
      const modalRect = modalRef.current.getBoundingClientRect();
      const mouseX = event.activatorEvent?.clientX || event.delta.x;
      const mouseY = event.activatorEvent?.clientY || event.delta.y;
      
      // Get current mouse position by adding delta to starting position
      const currentX = (event.activatorEvent?.clientX || 0) + (event.delta?.x || 0);
      const currentY = (event.activatorEvent?.clientY || 0) + (event.delta?.y || 0);
      
      const isInsideModal = (
        currentX >= modalRect.left &&
        currentX <= modalRect.right &&
        currentY >= modalRect.top &&
        currentY <= modalRect.bottom
      );
      
      console.log('🎯 Drag position check:', {
        modalRect: {
          left: modalRect.left,
          right: modalRect.right, 
          top: modalRect.top,
          bottom: modalRect.bottom
        },
        currentPosition: { x: currentX, y: currentY },
        isInsideModal
      });
      
      if (isInsideModal) {
        console.log('🟢 Dragging INSIDE modal area');
        setIsDraggingOutside(false);
      } else {
        console.log('🔵 Dragging OUTSIDE modal area');
        setIsDraggingOutside(true);
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    console.log('🏁 Drag ended:', { 
      activeId: active.id, 
      overId: over?.id, 
      hasOver: !!over,
      isDraggingOutside
    });
    
    setActiveId(null);

    // Only remove if we were dragging outside modal bounds
    if (isDraggingOutside) {
      console.log('🗑️ REMOVING from stack - was dragging outside modal bounds');
      // Parse the project ID - active.id format is 'stack-{uuid}' not 'stack-{number}'
      const projectIdString = active.id.replace('stack-', '');
      console.log('🔍 Parsing project ID:', { activeId: active.id, projectIdString });
      
      // Find the project by matching the ID as string (UUIDs are strings, not integers)
      const draggedProject = stack?.projects?.find(p => p.id.toString() === projectIdString);
      
      console.log('✅ REMOVING from stack - dragged outside modal:', { 
        projectIdString, 
        draggedProject: draggedProject?.name, 
        stackId: stack?.id,
        reason: !over ? 'no drop target' : isDraggingOutside ? 'was dragging outside' : 'unknown'
      });
      
      if (draggedProject && onRemoveFromStack && stack) {
        console.log('🔄 Calling onRemoveFromStack for project:', draggedProject.name);
        onRemoveFromStack(stack.id, draggedProject);
      }
    } else {
      console.log('❌ Not removing from stack - dropped on valid target:', over?.id);
    }
    
    setIsDraggingOutside(false);
  };

  // Find the active project by ID
  const activeProject = activeId && stack ? stack.projects.find(p => p.id === activeId) : null;

  if (!stack) return null;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="!max-w-7xl w-[95vw] max-h-[90vh] overflow-hidden p-0 sm:!max-w-7xl" style={{ backgroundColor: '#fcf8f3' }}>
            <div className="overflow-y-auto max-h-[90vh] p-6">
            <div ref={combinedRef}>
              <DialogHeader>
                <DialogTitle className="sr-only">
                  Stack: {stack.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-8">
                {/* Stack Header */}
                <div className="flex flex-col items-center space-y-4">
                  {/* Stack Image */}
                  <div 
                    className="relative group cursor-pointer"
                    onMouseEnter={() => setIsHoveringLogo(true)}
                    onMouseLeave={() => setIsHoveringLogo(false)}
                    onClick={() => {
                      // TODO: Implement logo edit functionality
                      console.log('Edit logo clicked');
                    }}
                  >
                    {stack.image ? (
                      <img 
                        src={stack.image} 
                        alt={stack.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      // Default colorful grid icon
                      <div className="w-16 h-16 grid grid-cols-2 gap-1">
                        <div className="bg-green-500 rounded-sm"></div>
                        <div className="bg-red-500 rounded-sm"></div>
                        <div className="bg-green-400 rounded-sm"></div>
                        <div className="bg-gray-400 rounded-sm"></div>
                      </div>
                    )}
                    
                    {/* Pen icon overlay on hover */}
                    <div className={`absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center transition-opacity ${
                      isHoveringLogo ? 'opacity-100' : 'opacity-0'
                    }`}>
                      <Pencil className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Stack Name */}
                  {isEditingName ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const newName = editedName.trim();
                            if (newName && newName !== stack.name) {
                              onUpdateStack?.(stack.id, { name: newName });
                            }
                            setIsEditingName(false);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditedName(stack.name);
                            setIsEditingName(false);
                          }
                        }}
                        className="text-2xl font-semibold text-center max-w-xs"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          const newName = editedName.trim();
                          if (newName && newName !== stack.name) {
                            onUpdateStack?.(stack.id, { name: newName });
                          }
                          setIsEditingName(false);
                        }}
                        type="button"
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setEditedName(stack.name);
                          setIsEditingName(false);
                        }}
                        type="button"
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  ) : (
                    <h2 
                      className="text-2xl font-semibold text-gray-900 text-center cursor-pointer hover:text-gray-700 transition-colors"
                      onClick={() => {
                        setEditedName(stack.name);
                        setIsEditingName(true);
                      }}
                    >
                      {stack.name}
                    </h2>
                  )}

                </div>

                {/* Project Cards Grid */}
                {stack.projects?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 justify-items-center">
                    {stack.projects?.map((project) => (
                      <ProjectCard 
                        key={project.id}
                        project={project}
                        onColorChange={() => {}}
                        onPin={() => {}}
                        isDragActive={false}
                        isDropTarget={false}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="text-gray-500 text-lg mb-6">This stack is empty</p>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        onDeleteStack?.(stack.id);
                        onClose();
                      }}
                      className="px-6"
                    >
                      Delete Stack
                    </Button>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-4 mt-6 border-t">
                {/* Left side - Info buttons */}
                <div className="flex space-x-8">
                  <button className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800">
                    <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white text-xs">◉</span>
                    </div>
                    <span>See spaces in Mission Control</span>
                  </button>
                  <button className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800">
                    <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center">
                      <span className="text-white text-xs">◐</span>
                    </div>
                    <span>See activity for these spaces</span>
                  </button>
                </div>
                
                {/* Right side - Disband Stack button */}
                <Button
                  variant="destructive"
                  onClick={() => {
                    onDeleteStack?.(stack.id);
                    onClose();
                  }}
                  className="px-4"
                >
                  Disband Stack
                </Button>
              </div>
            </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Drag Overlay - Must be inside DndContext */}
        <DragOverlay 
          dropAnimation={{
            duration: 200,
            easing: 'ease-out',
          }}
        >
          {activeProject ? (
            <div 
              className="transition-all duration-200 ease-out pointer-events-none fixed w-[300px]"
              style={{ 
                transform: dragOverlayAnimated 
                  ? 'rotate(6deg) scale(1.1)' 
                  : 'rotate(0deg) scale(1)',
                zIndex: 9999
              }}
            >
              <ProjectCard 
                project={activeProject}
                onColorChange={() => {}}
                onPin={() => {}}
                isDragActive={false}
                isDropTarget={false}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}